import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

interface VisibilityContextType {
  visible: boolean;
  toggleVisibility: () => void;
  mask: (value: string | number | null | undefined, placeholder?: string) => string;
}

const VisibilityContext = createContext<VisibilityContextType>({
  visible: true,
  toggleVisibility: () => {},
  mask: (v) => String(v ?? ""),
});

const STORAGE_KEY = "vazoul-visibility-mode";

export function VisibilityProvider({ children }: { children: ReactNode }) {
  const [visible, setVisible] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === null ? true : stored === "true";
    } catch {
      return true;
    }
  });

  // Sync CSS class on <html> for global CSS-based masking
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.classList.add("visibility-off");
    } else {
      root.classList.remove("visibility-off");
    }
  }, [visible]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(visible));
    } catch {}
  }, [visible]);

  const toggleVisibility = useCallback(() => setVisible((v) => !v), []);

  const mask = useCallback(
    (value: string | number | null | undefined, placeholder = "•••••") => {
      if (visible) return String(value ?? "");
      return placeholder;
    },
    [visible]
  );

  return (
    <VisibilityContext.Provider value={{ visible, toggleVisibility, mask }}>
      {children}
    </VisibilityContext.Provider>
  );
}

export function useVisibility() {
  return useContext(VisibilityContext);
}
